import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UserTagList, UserTag } from '@app/user/user.service';
import { GalleryItem } from '@app/gallery/gallery-item';
import { GalleryService } from '@app/gallery/gallery.service';
import { NotificationsService } from '@shared/notifications.service';
import { ChatRoom, CustomChatRoom, ResolutionCenterService, SearchUser } from '../../resolution-center.service';

@Component({
    selector: 'app-chat-room-modal',
    templateUrl: './chat-room-modal.component.html',
    styleUrls: ['./chat-room-modal.component.scss'],
    standalone: false
})

export class ChatRoomModalComponent implements OnInit, OnDestroy {
    @Input() chatRoom: ChatRoom | null = null; // If provided, we're editing; otherwise, creating
    @Input() step1FormData: ChatRoomFormData | null = null; // Step 1 data passed from parent
    @Input() userTagLists: UserTagList[] = [];
    @Input() initialStep: 1 | 2 = 1;
    @Output() close = new EventEmitter<void>();
    @Output() next = new EventEmitter<ChatRoomFormData>();
    @Output() created = new EventEmitter<ChatRoom>();
    @Output() updated = new EventEmitter<ChatRoom>();

    public currentStep = 1;
    public totalSteps = 2;
    public activeTab: 'tags' | 'status' | 'allUsers' = 'tags';
    public isSaving = false;
    public selectedTagIds: Set<number> = new Set();
    public selectedUserIds: Set<number> = new Set();
    public distinctUsersCount: number | null = null;
    public isCountingUsers = false;
    private countUsersDebounceTimer: any = null;
    
    // All users tab properties
    public searchUsersQuery: string = '';
    public searchUsersList: SearchUser[] = [];
    public searchUsersLoading = false;
    public searchUsersPage = 1;
    public searchUsersTotalPages = 1;
    public searchUsersTotal = 0;
    private searchUsersDebounceTimer: any = null;
    
    // Form data
    public formData: ChatRoomFormData = {
        name: '',
        description: '',
        image: null,
        imageUrl: '',
        is_read_only: false
    };

    // Tracks existing image when editing so we can reuse its id if user doesn't upload a new one
    private existingImageId: number | null = null;

    public isClosing = false;

    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private galleryService: GalleryService,
        private notifications: NotificationsService
    ) {}

    ngOnInit(): void {
        if (this.step1FormData) {
            // Step 1 data was passed from parent (we're on step 2)
            this.formData = { ...this.step1FormData };
            this.currentStep = 2;
        } else if (this.chatRoom) {
            // Populate form with existing chat room data (editing mode)
            this.formData = {
                name: this.chatRoom.name || '',
                description: this.chatRoom.description || '',
                image: null,
                imageUrl: (this.chatRoom.image as GalleryItem)?.thumbnail_url || '',
                is_read_only: this.chatRoom.is_read_only || false
            };

            const existingImage = this.chatRoom.image as GalleryItem | undefined;
            this.existingImageId = existingImage?.id ?? null;

            // Pre-populate selected tags if chatRoom has tags (ChatRoom interface)
            if ('tags' in this.chatRoom && this.chatRoom.tags) {
                this.chatRoom.tags.forEach(tag => {
                    this.selectedTagIds.add(tag.id);
                });
            }

            // Pre-populate selected users if chatRoom has members (ChatRoom interface)
            if ('members' in this.chatRoom && this.chatRoom.members) {
                this.chatRoom.members.forEach(member => {
                    this.selectedUserIds.add(member.id);
                });
            }

            // Update user count if we have selections
            if (this.selectedTagIds.size > 0 || this.selectedUserIds.size > 0) {
                this.updateDistinctUsersCount();
            }
        }

        if (this.initialStep === 2) {
            this.currentStep = 2;
        }
    }

    get modalTitle(): string {
        if (this.chatRoom) {
            const roomName = this.chatRoom.name || '';
            return `Update ${roomName} group chat`;
        }
        return 'Create new group chat';
    }

    get stepLabel(): string {
        if (this.currentStep === 1) {
            return `Step ${this.currentStep} of ${this.totalSteps}: Group setup`;
        }
        return `Step ${this.currentStep} of ${this.totalSteps}: Add members`;
    }

    onBackdropClick(event: MouseEvent): void {
        return;
        
        // Only close if clicking directly on the backdrop (not on modal content)
        if (event.target === event.currentTarget) {
            this.onClose();
        }
    }

    onClose(): void {
        this.isClosing = true;
        setTimeout(() => {
            this.close.emit();
            this.isClosing = false;
        }, 180);
    }

    onNext(): void {
        // Validate required fields
        if (!this.formData.name || this.formData.name.trim() === '') {
            return;
        }

        // Move to step 2
        this.currentStep = 2;
        this.next.emit({ ...this.formData });
    }

    onBack(): void {
        // Go back to step 1
        this.currentStep = 1;
    }

    async onCreate(): Promise<void> {
        if (this.isSaving) {
            return;
        }

        // Validate required fields
        if (!this.formData.name || this.formData.name.trim() === '') {
            return;
        }

        this.isSaving = true;

        try {
            // Decide which image_id to send:
            // - If user picked a new image file, upload it to /admin/gallery and use returned id
            // - Else, if editing and existing image wasn't removed, reuse its id
            let imageId: number | undefined;

            if (this.formData.image) {
                const uploaded = await this.uploadImageToGallery(this.formData.image);
                if (!uploaded) {
                    // Upload failed or was cancelled
                    this.isSaving = false;
                    return;
                }
                imageId = uploaded.id;
            } else if (this.chatRoom && this.existingImageId) {
                imageId = this.existingImageId;
            }

            // Prepare chat room data
            const chatRoomData: CustomChatRoom = {
                id: (this.chatRoom && 'id' in this.chatRoom) ? this.chatRoom.id : 0,
                name: this.formData.name,
                description: this.formData.description || undefined,
                image_id: imageId,
                is_read_only: this.formData.is_read_only
            };

            // Get selected tags and users
            const selectedTagIdsArray = Array.from(this.selectedTagIds);
            const selectedUserIdsArray = Array.from(this.selectedUserIds);

            const { data, error } = await this.resolutionCenterService.saveCustomChatRoom(
                chatRoomData,
                selectedTagIdsArray.length > 0 ? selectedTagIdsArray : undefined,
                selectedUserIdsArray.length > 0 ? selectedUserIdsArray : undefined
            );

            if (error || !data) {
                console.error('Error saving chat room:', error);
                this.notifications.error('Error', 'Something went wrong while saving the group chat.');
                this.isSaving = false;
                return;
            }

            // Emit the created/updated chat room
            if (this.chatRoom) {
                this.updated.emit(data);
            } else {
                this.created.emit(data);
            }

            // Close modal
            this.onClose();
        } catch (error) {
            console.error('Error saving chat room:', error);
            this.notifications.error('Error', 'Something went wrong while saving the group chat.');
        } finally {
            this.isSaving = false;
        }
    }

    get createButtonText(): string {
        if (this.chatRoom) {
            return 'Update chat';
        }
        return 'Create new chat';
    }

    onImageSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            
            // Validate file type
            if (!file.type.startsWith('image/')) {
                // TODO: Show error message
                return;
            }

            // Validate file size (e.g., max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                // TODO: Show error message
                return;
            }

            // Create preview
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.formData.imageUrl = e.target.result;
            };
            reader.readAsDataURL(file);

            this.formData.image = file;
        }
    }

    onImagePlaceholderClick(input: HTMLInputElement): void {
        input.click();
    }

    onLabelClick(event: Event): void {
        event.stopPropagation();
    }

    onRemoveImage(): void {
        this.formData.image = null;
        this.formData.imageUrl = '';
        this.existingImageId = null;
        // Reset file input
        const fileInput = document.getElementById('chat-room-image-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    isFormValid(): boolean {
        return !!(this.formData.name && this.formData.name.trim() !== '');
    }

    onUsersCanSendMessagesChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.formData.is_read_only = !target.checked;
    }

    private async uploadImageToGallery(file: File): Promise<GalleryItem | null> {
        const nameParts = file.name.split('.');
        nameParts.pop();
        const baseName = nameParts.join('.') || 'chat-room-image';

        const upload$ = await this.galleryService.upload(baseName, file, false, 'image');

        return new Promise<GalleryItem | null>((resolve) => {
            upload$.subscribe(
                res => {
                    if (res.response && res.response.data) {
                        resolve(res.response.data as GalleryItem);
                    }
                },
                error => {
                    const err =
                        error.status >= 500
                            ? error.statusText
                            : (error.error?.errorMessages || []).join('\r\n') || 'Image upload failed';
                    this.notifications.error('Error', `Something went wrong while uploading the image. ${err}`);
                    resolve(null);
                }
            );
        });
    }

    get customTagsList(): UserTagList | null {
        const customTagList = this.userTagLists.find(tagList => tagList.label === 'custom');
        return customTagList || null;
    }

    get customTags(): UserTag[] {
        const customTagList = this.customTagsList;
        return customTagList?.tags || [];
    }

    get subscriptionTagsList(): UserTagList | null {
        const subscriptionTagList = this.userTagLists.find(tagList => tagList.label === 'subscription');
        return subscriptionTagList || null;
    }

    get subscriptionTags(): UserTag[] {
        const subscriptionTagList = this.subscriptionTagsList;
        return subscriptionTagList?.tags || [];
    }

    // Check if tag is selected
    isTagSelected(tagId: number): boolean {
        return this.selectedTagIds.has(tagId);
    }

    // Toggle tag selection
    toggleTagSelection(tagId: number): void {
        if (this.selectedTagIds.has(tagId)) {
            this.selectedTagIds.delete(tagId);
        } else {
            this.selectedTagIds.add(tagId);
        }
        // Trigger debounced user count update
        this.updateDistinctUsersCount();
        
        // If on All Users tab, reload users to update is_selected flags
        if (this.activeTab === 'allUsers') {
            this.loadSearchUsers();
        }
    }

    // Update distinct users count with debouncing
    public updateDistinctUsersCount(): void {
        // Clear existing timer
        if (this.countUsersDebounceTimer) {
            clearTimeout(this.countUsersDebounceTimer);
        }

        // If no tags and no users selected, reset count
        if (this.selectedTagIds.size === 0 && this.selectedUserIds.size === 0) {
            this.distinctUsersCount = null;
            this.isCountingUsers = false;
            return;
        }

        // Set loading state
        this.isCountingUsers = true;

        // Debounce the API call (wait 300ms after last change)
        this.countUsersDebounceTimer = setTimeout(() => {
            this.fetchDistinctUsersCount();
        }, 300);
    }

    // Fetch distinct users count from backend
    private async fetchDistinctUsersCount(): Promise<void> {
        // Get all selected tags from both tabs (custom and subscription)
        const allSelectedTags: UserTag[] = [
            ...this.customTags.filter(tag => this.selectedTagIds.has(tag.id)),
            ...this.subscriptionTags.filter(tag => this.selectedTagIds.has(tag.id))
        ];
        
        const tagIds = allSelectedTags.map(tag => tag.id);
        const selectedUserIdsArray = Array.from(this.selectedUserIds);
        
        // If no tags and no users selected, reset count
        if (tagIds.length === 0 && selectedUserIdsArray.length === 0) {
            this.distinctUsersCount = null;
            this.isCountingUsers = false;
            return;
        }

        try {
            const { data, error } = await this.resolutionCenterService.countDistinctUsersByTags(
                tagIds.length > 0 ? tagIds : undefined,
                selectedUserIdsArray.length > 0 ? selectedUserIdsArray : undefined
            );
            
            if (error) {
                console.warn('Failed to fetch distinct users count, falling back to sum:', error);
                // Fallback to sum if API fails
                this.distinctUsersCount = this.getFallbackUsersCount();
            } else {
                this.distinctUsersCount = data;
            }
        } catch (error) {
            console.warn('Error fetching distinct users count, falling back to sum:', error);
            // Fallback to sum on error
            this.distinctUsersCount = this.getFallbackUsersCount();
        } finally {
            this.isCountingUsers = false;
        }
    }

    // Fallback: calculate sum of items_count (may be inaccurate due to overlaps)
    // Includes all selected tags from both tabs
    private getFallbackUsersCount(): number {
        const allSelectedTags: UserTag[] = [
            ...this.customTags.filter(tag => this.selectedTagIds.has(tag.id)),
            ...this.subscriptionTags.filter(tag => this.selectedTagIds.has(tag.id))
        ];
        
        return allSelectedTags.reduce((sum, tag) => sum + (tag.items_count || 0), 0);
    }

    // Calculate total users count from selected tags and users
    // Uses distinct count from API if available, otherwise falls back to sum
    get totalUsersCount(): number {
        // If we have a distinct count from API, use it (this includes both tags and manually selected users)
        if (this.distinctUsersCount !== null) {
            return this.distinctUsersCount;
        }
        
        // Otherwise, use fallback sum (may be inaccurate)
        // Note: This only counts tags, manually selected users are handled by API
        return this.getFallbackUsersCount();
    }

    // Get selected tags count for current tab
    get selectedTagsCount(): number {
        if (this.activeTab === 'tags') {
            return this.customTags.filter(tag => this.selectedTagIds.has(tag.id)).length;
        } else if (this.activeTab === 'status') {
            return this.subscriptionTags.filter(tag => this.selectedTagIds.has(tag.id)).length;
        }
        return 0;
    }

    // Check if there are any selections (tags or users) across all tabs
    get hasAnySelections(): boolean {
        return this.selectedTagIds.size > 0 || this.selectedUserIds.size > 0;
    }

    // All users tab methods
    onAllUsersTabActivated(): void {
        this.loadSearchUsers();
    }

    onSearchUsersInput(): void {
        // Clear existing timer
        if (this.searchUsersDebounceTimer) {
            clearTimeout(this.searchUsersDebounceTimer);
        }

        // Reset to first page on new search
        this.searchUsersPage = 1;

        // Debounce the search (wait 500ms after last keystroke)
        this.searchUsersDebounceTimer = setTimeout(() => {
            this.loadSearchUsers();
        }, 500);
    }

    clearSearchUsers(): void {
        this.searchUsersQuery = '';
        this.searchUsersPage = 1;
        this.loadSearchUsers();
    }

    getInitials(name?: string): string {
        if (!name) return '?';
        const words = name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    async loadSearchUsers(): Promise<void> {
        if (this.activeTab !== 'allUsers') {
            return;
        }

        this.searchUsersLoading = true;

        try {
            const selectedTagIdsArray = Array.from(this.selectedTagIds);
            const selectedUserIdsArray = Array.from(this.selectedUserIds);

            const { data, error } = await this.resolutionCenterService.searchUsersForAddingToChatRoom(
                this.searchUsersQuery,
                selectedTagIdsArray,
                selectedUserIdsArray,
                this.searchUsersPage
            );

            if (error) {
                console.error('Error loading users:', error);
                this.searchUsersList = [];
                return;
            }

            if (data) {
                this.searchUsersList = data.data || [];
                this.searchUsersTotalPages = data.last_page || 1;
                this.searchUsersTotal = data.total || 0;

                // Update selectedUserIds based on is_selected flag from backend
                this.searchUsersList.forEach(user => {
                    if (user.is_selected) {
                        this.selectedUserIds.add(user.id);
                    }
                });
            }
        } catch (error) {
            console.error('Error loading users:', error);
            this.searchUsersList = [];
        } finally {
            this.searchUsersLoading = false;
        }
    }

    toggleUserSelection(userId: number): void {
        if (this.selectedUserIds.has(userId)) {
            this.selectedUserIds.delete(userId);
        } else {
            this.selectedUserIds.add(userId);
        }

        // Update the user's is_selected flag in the list locally
        const user = this.searchUsersList.find(u => u.id === userId);
        if (user) {
            user.is_selected = this.selectedUserIds.has(userId);
        }

        // Trigger debounced user count update (includes tags and users)
        this.updateDistinctUsersCount();
    }

    isUserSelected(userId: number): boolean {
        return this.selectedUserIds.has(userId);
    }

    get selectedUsersCount(): number {
        return this.selectedUserIds.size;
    }

    getPaginationPages(): (number | string)[] {
        const pages: (number | string)[] = [];
        const totalPages = this.searchUsersTotalPages;
        const currentPage = this.searchUsersPage;

        if (totalPages <= 7) {
            // Show all pages if 7 or fewer
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    }

    isPaginationPageNumber(page: number | string): page is number {
        return typeof page === 'number';
    }

    onPaginationPageClick(page: number | string): void {
        if (typeof page === 'number') {
            this.onSearchUsersPageChange(page);
        }
    }

    onSearchUsersPageChange(page: number): void {
        if (page < 1 || page > this.searchUsersTotalPages) {
            return;
        }
        this.searchUsersPage = page;
        this.loadSearchUsers();
        // Scroll to top of list
        const tabContent = document.querySelector('.tab-content');
        if (tabContent) {
            tabContent.scrollTop = 0;
        }
    }

    ngOnDestroy(): void {
        // Clear debounce timers on component destroy
        if (this.countUsersDebounceTimer) {
            clearTimeout(this.countUsersDebounceTimer);
        }
        if (this.searchUsersDebounceTimer) {
            clearTimeout(this.searchUsersDebounceTimer);
        }
    }
}

export interface ChatRoomFormData {
    name: string;
    description: string;
    image: File | null;
    imageUrl: string;
    is_read_only: boolean;
}
